import { useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification, Divider, FormLabel } from "@impero-com/ui";
import { UseFormReturn } from "react-hook-form";

import {
    ControlProgramTagCategoryView,
    ControlProgramTagView,
} from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";

import { useCreateControlProgramTagMutation } from "../queries/useCreateControlProgramTagMutation";
import { useDeleteControlProgramTagMutation } from "../queries/useDeleteControlProgramTagMutation";

import { ControlProgramTagManagementList } from "./ControlProgramTagManagementList";
import { ControlProgramTagCategoryFormSchema } from "./useControlProgramTagCategoryFormSchema";

interface ControlProgramTagCategoryFormProps {
    form: UseFormReturn<ControlProgramTagCategoryFormSchema>;
    category: ControlProgramTagCategoryView;
    tags: ControlProgramTagView[];
    setTags: React.Dispatch<React.SetStateAction<ControlProgramTagView[]>>;
    existingTagNames: string[];
}

export function ControlProgramTagCategoryForm({
    form,
    category,
    tags,
    setTags,
    existingTagNames,
}: ControlProgramTagCategoryFormProps) {
    const { t } = useTranslation();
    const { mutateAsync: createTag } = useCreateControlProgramTagMutation();
    const { mutateAsync: deleteTag } = useDeleteControlProgramTagMutation();
    const categoryId = category.id;
    const { control, handleSubmit, reset } = form;

    const handleAddTag = handleSubmit(async (data: { newTagName: string }) => {
        try {
            const { data: newTag } = await createTag({
                data: {
                    name: data.newTagName,
                    controlProgramTagCategoryId: categoryId,
                },
            });
            setTags((prevTags) => [newTag, ...prevTags]);
            reset(); // Clear the input field
        } catch (error) {
            console.error("Failed to create tag:", error);
            emitNotification(
                "error",
                t("TagsList_EditCategoryDrawer_Notification_CreateTagFailed")
            );
        }
    });

    const handleDeleteTag = (tagId: string) => {
        try {
            deleteTag({ tagId });
            setTags((prevTags) => prevTags.filter((tag) => tag.id !== tagId));
        } catch (error) {
            console.error("Failed to delete tag:", error);
            emitNotification(
                "error",
                t("TagsList_EditCategoryDrawer_Notification_DeleteTagFailed")
            );
        }
    };

    return (
        <>
            <BaseFormDisplay
                label={t("TagsList_EditCategoryDrawer_Field_CategoryName")}
            >
                <p>{category.name}</p>
            </BaseFormDisplay>
            <Divider />
            <FormLabel>
                {t("TagsList_EditCategoryDrawer_Field_ProgramTags")}
            </FormLabel>
            <ControlProgramTagManagementList
                control={control}
                tags={tags}
                handleAddTag={handleAddTag}
                handleDeleteTag={handleDeleteTag}
                existingTagNames={existingTagNames}
            />
        </>
    );
}
